import { jsPDF } from "jspdf";
import { PAGE, substitute } from "./templates";

const A5 = { w: 148, h: 210 };

function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return [17, 17, 17];
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// jsPDF only accepts PNG/JPEG; rasterise anything else (SVG, WebP) via canvas.
async function pdfImage(src) {
  const img = await loadImage(src);
  const natW = img.naturalWidth || img.width || 1;
  const natH = img.naturalHeight || img.height || 1;
  if (/^data:image\/(png|jpeg|jpg);/i.test(src)) {
    return { data: src, format: /png/i.test(src.slice(0, 20)) ? "PNG" : "JPEG", natW, natH };
  }
  const scale = Math.max(1, 1600 / Math.max(natW, natH));
  const c = document.createElement("canvas");
  c.width = Math.round(natW * scale);
  c.height = Math.round(natH * scale);
  c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
  return { data: c.toDataURL("image/png"), format: "PNG", natW, natH };
}

export function fitContain(natW, natH, boxW, boxH) {
  const r = Math.min(boxW / natW, boxH / natH);
  const w = natW * r, h = natH * r;
  return { dx: (boxW - w) / 2, dy: (boxH - h) / 2, w, h };
}

export const resolveSrc = (el, assets = {}) => {
  const src = typeof el === "string" ? el : el?.src;
  const label = typeof el === "string" ? "" : el?.label || "";
  if (src === "club_logo") return assets.logo || "";
  if (src === "club_signature" || (!src && /signature/i.test(label))) return assets.signature || "";
  return src || "";
};

async function drawDesign(doc, design, row, assets, t, imgCache) {
  const { s, ox, oy } = t;
  const X = (v) => ox + v * s;
  const Y = (v) => oy + v * s;
  if (design.background && design.background.toLowerCase() !== "#ffffff") {
    doc.setFillColor(...hexToRgb(design.background));
    doc.rect(ox, oy, PAGE.w * s, PAGE.h * s, "F");
  }
  for (const el of design.elements) {
    if (el.type === "text") {
      const style = el.bold && el.italic ? "bolditalic" : el.bold ? "bold" : el.italic ? "italic" : "normal";
      doc.setFont(el.font || "helvetica", style);
      doc.setFontSize((el.fontSize || 16) * s);
      doc.setTextColor(...hexToRgb(el.color));
      const text = substitute(el.text, row);
      const lines = doc.splitTextToSize(text, el.w * s);
      const ax = el.align === "center" ? X(el.x + el.w / 2) : el.align === "right" ? X(el.x + el.w) : X(el.x);
      doc.text(lines, ax, Y(el.y), { align: el.align || "left", baseline: "top", lineHeightFactor: 1.2 });
    } else if (el.type === "line") {
      doc.setDrawColor(...hexToRgb(el.color));
      doc.setLineWidth((el.strokeWidth || 0.4) * s);
      doc.line(X(el.x), Y(el.y), X(el.x + el.w), Y(el.y));
    } else if (el.type === "rect") {
      doc.setDrawColor(...hexToRgb(el.color));
      doc.setLineWidth((el.strokeWidth || 0.4) * s);
      if (el.fill) doc.setFillColor(...hexToRgb(el.fill));
      doc.rect(X(el.x), Y(el.y), el.w * s, el.h * s, el.fill ? "FD" : "S");
    } else if (el.type === "image") {
      const src = resolveSrc(el, assets);
      if (!src) continue;
      try {
        if (!imgCache.has(src)) imgCache.set(src, await pdfImage(src));
        const im = imgCache.get(src);
        const f = fitContain(im.natW, im.natH, el.w, el.h);
        doc.addImage(im.data, im.format, X(el.x + f.dx), Y(el.y + f.dy), f.w * s, f.h * s);
      } catch (_) { /* unreadable image — skip, keep the PDF */ }
    }
  }
}

// layout: "a4" (one certificate per A4 portrait) | "a5x2" (two A5 side by side on A4 landscape)
export async function buildPdf({ design, rows, layout, assets }) {
  const imgCache = new Map();
  if (layout === "a5x2") {
    const doc = new jsPDF("l", "mm", "a4");
    const s = A5.w / PAGE.w;
    const oy = (A5.h - PAGE.h * s) / 2;
    for (let i = 0; i < rows.length; i += 2) {
      if (i > 0) doc.addPage();
      await drawDesign(doc, design, rows[i], assets, { s, ox: 0.5, oy }, imgCache);
      if (rows[i + 1]) await drawDesign(doc, design, rows[i + 1], assets, { s, ox: 148.5, oy }, imgCache);
      doc.setDrawColor(190);
      doc.setLineWidth(0.2);
      doc.setLineDashPattern([2, 2], 0);
      doc.line(148.5, 4, 148.5, 206);
      doc.setLineDashPattern([], 0);
    }
    return doc;
  }
  const doc = new jsPDF("p", "mm", "a4");
  for (let i = 0; i < rows.length; i += 1) {
    if (i > 0) doc.addPage();
    await drawDesign(doc, design, rows[i], assets, { s: 1, ox: 0, oy: 0 }, imgCache);
  }
  return doc;
}

// Resize an uploaded image so saved designs stay small (max edge in px).
export function fileToDataUrl(file, maxPx = 900) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = async () => {
      try {
        const img = await loadImage(reader.result);
        const r = Math.min(1, maxPx / Math.max(img.naturalWidth, img.naturalHeight));
        const c = document.createElement("canvas");
        c.width = Math.max(1, Math.round(img.naturalWidth * r));
        c.height = Math.max(1, Math.round(img.naturalHeight * r));
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        const isJpeg = /jpe?g/i.test(file.type);
        resolve(isJpeg ? c.toDataURL("image/jpeg", 0.85) : c.toDataURL("image/png"));
      } catch (e) { reject(e); }
    };
    reader.readAsDataURL(file);
  });
}
